import { API_BASE_URL } from '@synergy/config';
import { AutocompleteOption, PoolFormikAutocomplete, PoolFormikAutocompleteProps } from '@synergy/components-next';
import { FormattedMessage, useIntl } from 'react-intl';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { getLocalizedString } from '@synergy/utils';

import { PaymentTerm } from '../../models/payment-term';
import { PaymentTermsApi } from '../../api/payment-terms-api';
import { useAppSelector } from '../../../store/use-appstate-selector';

export interface PaymentTermAutocompleteProps
    extends Omit<
        PoolFormikAutocompleteProps<PaymentTerm | number>,
        'options' | 'renderValue' | 'classes' | 'onValueChange'
    > {
    useId?: boolean;
    onValueChange?: (paymentTerm: PaymentTerm | null) => void;
}

export interface PaymentTermAutocompleteRef {
    getPaymentTerm: (id?: number) => PaymentTerm | null;
}

export const PaymentTermAutocomplete = forwardRef<PaymentTermAutocompleteRef, PaymentTermAutocompleteProps>(
    (props, ref) => {
        const { onValueChange, label, useId, ...restProps } = props;
        const api = useRef(new PaymentTermsApi(API_BASE_URL)).current;
        const [paymentTerms, setPaymentTerms] = useState<PaymentTerm[] | null>(null);
        const locales = useAppSelector(state => state.supportedLocales);
        const intl = useIntl();

        useImperativeHandle(ref, () => ({
            getPaymentTerm: (id?: number) => paymentTerms?.find(paymentTerm => paymentTerm.id === id) ?? null,
        }));

        useEffect(() => {
            const asyncEffect = async () => {
                if (!paymentTerms) {
                    const response = await api.cgetAll(new URLSearchParams());
                    setPaymentTerms(response.success ? response.result : []);
                }
            };
            asyncEffect();
        }, [api, paymentTerms]);

        const getDisplayValue = (value: PaymentTerm | number | null): string => {
            if (typeof value === 'number') {
                return `${value}`;
            }

            return value ? `${value.id} : ${getLocalizedString(locales, value.text)}` : '';
        };

        const options: AutocompleteOption<PaymentTerm | number>[] =
            paymentTerms
                ?.sort((a, b) => a.id - b.id)
                ?.map(paymentTerm => ({
                    value: useId ? paymentTerm.id : paymentTerm,
                    displayValue: getDisplayValue(paymentTerm),
                })) ?? [];

        const handleSelected = (value: PaymentTerm | number | null): void => {
            if (!value) {
                return;
            }

            if (typeof value === 'number') {
                const term = paymentTerms?.find(paymentTerm => paymentTerm.id === value);
                onValueChange?.(term ?? null);
            } else {
                onValueChange?.(value);
            }
        };

        const getValueFunction = (value: PaymentTerm | number | null): PaymentTerm | number | null => {
            if (!value) {
                return null;
            }

            if (typeof value === 'number') {
                return value;
            } else {
                return paymentTerms?.find(paymentTerm => paymentTerm.id === value.id) ?? null;
            }
        };

        return (
            <PoolFormikAutocomplete<PaymentTerm | number>
                placeholder={intl.formatMessage({ id: 'app.label.paymentTerm' })}
                label={label ?? <FormattedMessage id="app.label.paymentTerm" />}
                options={options}
                fullWidth={true}
                {...restProps}
                onChange={handleSelected}
                getValue={getValueFunction}
                getDisplayValue={getDisplayValue}
            />
        );
    }
);
